import React, { useState, useRef, useEffect } from 'react';
import {
  countries,
  getSortedCountries,
  parsePhoneNumber,
  validatePhoneNumber,
  Country
} from '@shared/data/countries';

interface PhoneInputProps {
  value?: string;
  onChange: (value: string) => void;
  onValidation?: (isValid: boolean) => void;
  placeholder?: string;
  required?: boolean;
  disabled?: boolean;
  error?: string | null;
  className?: string;
}

export const PhoneInput: React.FC<PhoneInputProps> = ({
  value = '',
  onChange,
  onValidation,
  placeholder = 'Phone number',
  required = false,
  disabled = false,
  error,
  className = ''
}) => {
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  // Initialize with Estonia as default country to ensure it's set on first render
  const [selectedCountry, setSelectedCountry] = useState<Country | undefined>(
    () => countries.find(c => c.code === 'EE')
  );
  const [phoneNumber, setPhoneNumber] = useState('');
  const [isValid, setIsValid] = useState(false);

  const dropdownRef = useRef<HTMLDivElement>(null);
  const searchInputRef = useRef<HTMLInputElement>(null);
  const isInternalChange = useRef(false);
  const lastValueRef = useRef(value);

  // Initialize from value prop
  useEffect(() => {
    // Skip if this update was triggered by our own onChange call
    if (isInternalChange.current) {
      isInternalChange.current = false;
      lastValueRef.current = value;
      return;
    }

    // Only update if the value actually changed externally
    if (value !== lastValueRef.current) {
      lastValueRef.current = value;
      if (value) {
        const parsed = parsePhoneNumber(value);
        setSelectedCountry(parsed.country || countries.find(c => c.code === 'EE'));
        setPhoneNumber(parsed.number);
      } else {
        // Set Estonia as default
        setSelectedCountry(countries.find(c => c.code === 'EE'));
        setPhoneNumber('');
      }
    }
  }, [value]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsDropdownOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Focus search input when dropdown opens
  useEffect(() => {
    if (isDropdownOpen && searchInputRef.current) {
      setTimeout(() => searchInputRef.current?.focus(), 100);
    }
  }, [isDropdownOpen]);

  // Update parent component when phone number changes
  useEffect(() => {
    if (selectedCountry) {
      const fullNumber = phoneNumber ? `${selectedCountry.phonePrefix}${phoneNumber}` : '';

      // Only call onChange if the value actually changed
      if (fullNumber !== lastValueRef.current) {
        isInternalChange.current = true;
        lastValueRef.current = fullNumber;
        onChange(fullNumber);
      }

      // Validate the phone number
      const valid = phoneNumber ? validatePhoneNumber(selectedCountry, phoneNumber) : false;
      setIsValid(valid);
      onValidation?.(valid);
    }
  }, [selectedCountry, phoneNumber, onChange, onValidation]);

  const handleCountrySelect = (country: Country) => {
    setSelectedCountry(country);
    setIsDropdownOpen(false);
    setSearchTerm('');
  };

  const handlePhoneNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    // Allow only digits and common phone characters
    const value = e.target.value.replace(/[^\d\s\-()]/g, '');
    setPhoneNumber(value);
  };

  const handlePhoneNumberPaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
    e.preventDefault();
    const pastedText = e.clipboardData.getData('text');

    // Check if pasted text contains a full phone number with country code
    const parsed = parsePhoneNumber(pastedText);
    if (parsed.country && parsed.number) {
      setSelectedCountry(parsed.country);
      setPhoneNumber(parsed.number);
    } else {
      // Just paste the cleaned number
      const cleanedNumber = pastedText.replace(/[^\d\s\-()]/g, '');
      setPhoneNumber(cleanedNumber);
    }
  };

  // Filter countries based on search term
  const filteredCountries = getSortedCountries().filter(country => {
    const term = searchTerm.toLowerCase();
    return (
      country.name.toLowerCase().includes(term) ||
      country.code.toLowerCase().includes(term) ||
      country.phonePrefix.includes(term)
    );
  });

  // Get placeholder based on selected country
  const getPlaceholder = () => {
    if (!selectedCountry) return placeholder;

    switch (selectedCountry.code) {
      case 'EE':
        return '5123 4567';
      case 'FI':
        return '412 345678';
      case 'LV':
      case 'LT':
        return '2123 4567';
      case 'US':
      case 'CA':
        return '(555) 123-4567';
      case 'GB':
        return '7911 123456';
      default:
        return placeholder;
    }
  };

  return (
    <div className={`phone-input-container ${className}`}>
      <div className="flex space-x-2">
        {/* Country Selector */}
        <div className="relative" ref={dropdownRef}>
          <button
            type="button"
            onClick={() => setIsDropdownOpen(!isDropdownOpen)}
            disabled={disabled}
            className={`
              flex items-center space-x-2 px-3 h-10 border rounded-md bg-white
              ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'hover:bg-gray-50 cursor-pointer'}
              ${error ? 'border-red-300' : 'border-gray-300'}
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            `}
          >
            {selectedCountry && (
              <>
                <span className="text-lg">{selectedCountry.flag}</span>
                <span className="text-sm font-medium">{selectedCountry.phonePrefix}</span>
                <svg
                  className={`w-4 h-4 transition-transform ${isDropdownOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                </svg>
              </>
            )}
          </button>

          {/* Dropdown */}
          {isDropdownOpen && (
            <div className="absolute z-50 mt-1 w-72 bg-white border border-gray-200 rounded-md shadow-lg max-h-96 overflow-hidden">
              {/* Search Input */}
              <div className="p-2 border-b">
                <input
                  ref={searchInputRef}
                  type="text"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder="Search by country, code, or prefix..."
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Countries List */}
              <div className="overflow-y-auto max-h-64">
                {filteredCountries.length > 0 ? (
                  filteredCountries.map((country, index) => {
                    const isPriority = ['EE', 'FI', 'LV', 'LT'].includes(country.code);
                    const isLastPriority = isPriority &&
                      (index === filteredCountries.length - 1 ||
                       !['EE', 'FI', 'LV', 'LT'].includes(filteredCountries[index + 1]?.code));

                    return (
                      <React.Fragment key={country.code}>
                        <button
                          type="button"
                          onClick={() => handleCountrySelect(country)}
                          className={`
                            w-full px-3 py-2 flex items-center space-x-3 hover:bg-gray-50
                            ${selectedCountry?.code === country.code ? 'bg-blue-50' : ''}
                            ${isPriority ? 'font-medium' : ''}
                          `}
                        >
                          <span className="text-lg">{country.flag}</span>
                          <span className="flex-1 text-left">{country.name}</span>
                          <span className="text-sm text-gray-500">{country.phonePrefix}</span>
                        </button>
                        {isLastPriority && (
                          <div className="border-t border-gray-200 my-1"></div>
                        )}
                      </React.Fragment>
                    );
                  })
                ) : (
                  <div className="p-3 text-center text-gray-500">
                    No countries found
                  </div>
                )}
              </div>
            </div>
          )}
        </div>

        {/* Phone Number Input */}
        <div className="relative flex-1">
          <input
            type="tel"
            value={phoneNumber}
            onChange={handlePhoneNumberChange}
            onPaste={handlePhoneNumberPaste}
            placeholder={getPlaceholder()}
            required={required}
            disabled={disabled}
            className={`
              w-full px-3 h-10 border rounded-md
              ${disabled ? 'bg-gray-100 cursor-not-allowed' : 'bg-white'}
              ${error ? 'border-red-300' : isValid && phoneNumber ? 'border-green-300' : 'border-gray-300'}
              focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500
            `}
          />
          {/* Validation indicator */}
          {phoneNumber && !disabled && (
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2">
              {isValid ? (
                <svg className="h-5 w-5 text-green-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                </svg>
              ) : (
                <svg className="h-5 w-5 text-red-500" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Error Message */}
      {error && (
        <p className="mt-1 text-sm text-red-600">{error}</p>
      )}
    </div>
  );
};

export default PhoneInput;